import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PageLayout from "../../components/layout/PageLayout";
import {
  useLazyGetOrdersQuery,
  useLazyGetOrderByIdQuery,
  useLazyGetPaymentTypesQuery,
  useLazyGetInvoiceByIdQuery,
  useCreateInvoiceMutation,
  useUpdateInvoiceMutation,
} from "../../store/api";
import { extractList } from "../../utils/response";
import { useLazySelectOptions } from "../../hooks/useLazySelectOptions";
import { useServerValidationErrors } from "../../hooks/useServerValidationErrors";
import { toast } from "react-toastify";
import { buildInvoiceRelationParams } from "./invoiceApiHelpers";
import { isInvoiceStatusPartial } from "./invoiceFieldParsers";

/** Default payment type: Card — match slug/code/name (API shapes vary). */
const pickCardPaymentType = (list) => {
  if (!Array.isArray(list) || list.length === 0) return null;
  const lower = (p) => String(p.slug ?? p.code ?? "").toLowerCase();
  const bySlugOrCode = list.find((p) => {
    const l = lower(p);
    return l === "card" || l === "credit_card" || l === "credit-card";
  });
  if (bySlugOrCode) return bySlugOrCode;
  const byNameExact = list.find((p) => {
    const n = String(p.name || "")
      .trim()
      .toLowerCase();
    return n === "card" || n === "credit card";
  });
  if (byNameExact) return byNameExact;
  const byNameContains = list.find((p) =>
    /\bcard\b/i.test(String(p.name || "")),
  );
  if (byNameContains) return byNameContains;
  return null;
};

/** Payload: order_id, payment_type_id, amount, expires_at, note */
const buildFormData = (values) => {
  const fd = new FormData();
  fd.append("order_id", values.order_id);
  fd.append("payment_type_id", values.payment_type_id);
  fd.append("amount", String(values.amount));
  if (values.expires_at) {
    fd.append("expires_at", values.expires_at);
  }
  fd.append("note", values.note ?? "");
  return fd;
};

const extractOrderId = (inv) => inv?.order_id ?? inv?.order?.id ?? null;
const extractPaymentTypeId = (inv) =>
  inv?.payment_type_id ?? inv?.paymentType?.id ?? inv?.payment_type?.id ?? null;

const shortId = (id) => {
  const s = String(id ?? "");
  return s.length > 12 ? `${s.slice(0, 8)}…` : s;
};

const formatOrderLabel = (order) => {
  if (!order) return "";
  const leadName = order.lead?.name || order.lead?.dba_name || "";
  const status = order.status ? String(order.status) : "";
  const amount =
    order.amount != null && order.amount !== "" ? `$${order.amount}` : "";
  const parts = [leadName, status, amount].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : `Order ${shortId(order.id)}`;
};

/** HTML date (YYYY-MM-DD) → API style DD-MM-YYYY */
const formatExpiresAtForApi = (isoDate) => {
  if (!isoDate) return "";
  const m = String(isoDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(isoDate);
  return `${m[3]}-${m[2]}-${m[1]}`;
};

const parseExpiresAtFromApi = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const dmy = s.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return "";
};

const CreateInvoice = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(editId);

  const orderIdFromQuery = searchParams.get("orderId") || "";
  const isOrderLocked = !isEdit && Boolean(orderIdFromQuery);

  const [loadingPage, setLoadingPage] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [lockedOrderResolving, setLockedOrderResolving] = useState(
    () => isOrderLocked,
  );

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  const [invoiceRecord, setInvoiceRecord] = useState(null);
  const { setErrors, applyApiError, clearError, fieldProps } =
    useServerValidationErrors();
  const hydratedInvoiceIdRef = useRef(null);

  const [fetchOrdersTrigger] = useLazyGetOrdersQuery();
  const [fetchOrderById] = useLazyGetOrderByIdQuery();
  const [fetchPaymentTypes] = useLazyGetPaymentTypesQuery();
  const [fetchInvoiceById] = useLazyGetInvoiceByIdQuery();
  const [createInvoice] = useCreateInvoiceMutation();
  const [updateInvoice] = useUpdateInvoiceMutation();

  const orderSelect = useLazySelectOptions(fetchOrdersTrigger, {
    args: {
      "relations[0]": "lead",
      paginate: 1,
      per_page: 500,
    },
    mapResult: (data) => {
      const list = extractList(data);
      return (Array.isArray(list) ? list : []).map((o) => ({
        id: o.id,
        label: formatOrderLabel(o),
        raw: o,
      }));
    },
    enabled: !isOrderLocked,
  });

  const paymentTypeSelect = useLazySelectOptions(fetchPaymentTypes, {
    args: { paginate: 0 },
    mapResult: (ptRes) => {
      const arr = extractList(ptRes);
      return (Array.isArray(arr) ? arr : []).map((p) => ({
        id: p.id,
        label: p.name ?? String(p.id),
        raw: p,
      }));
    },
  });

  const loadOrders = useCallback(
    () =>
      orderSelect.load().catch(() => {
        toast.error("Failed to load orders");
      }),
    [orderSelect],
  );

  const handlePaymentTypeOpen = useCallback(async () => {
    try {
      const options = await paymentTypeSelect.load();
      if (!isEdit && !selectedPaymentType && options.length) {
        const cardPt = pickCardPaymentType(options.map((o) => o.raw));
        if (cardPt) {
          setSelectedPaymentType({
            id: cardPt.id,
            label: cardPt.name ?? String(cardPt.id),
            raw: cardPt,
          });
        }
      }
    } catch {
      toast.warn("Could not load payment types — verify /payment-types endpoint");
    }
  }, [paymentTypeSelect, isEdit, selectedPaymentType]);

  useEffect(() => {
    if (isEdit || !isOrderLocked) return;
    let cancelled = false;
    setLockedOrderResolving(true);
    (async () => {
      try {
        // GET /orders/{id} eager-loads its relations server-side — `relations` is ignored here
        const data = await fetchOrderById({ id: orderIdFromQuery }).unwrap();
        const order = data?.data ?? data;
        if (cancelled) return;
        if (!order?.id) {
          toast.error("Order not found");
          setSelectedOrder(null);
          return;
        }
        setSelectedOrder({
          id: order.id,
          label: formatOrderLabel(order),
          raw: order,
        });
      } catch (err) {
        if (!cancelled) {
          toast.error(err?.data?.message || "Failed to load order");
          setSelectedOrder(null);
        }
      } finally {
        if (!cancelled) setLockedOrderResolving(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, isOrderLocked, orderIdFromQuery, fetchOrderById]);

  useEffect(() => {
    if (!isEdit || !editId) {
      setLoadingPage(false);
      setInvoiceRecord(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingPage(true);
      try {
        const data = await fetchInvoiceById({
          id: editId,
          params: buildInvoiceRelationParams(),
        }).unwrap();
        const inv = data?.data ?? data;
        if (cancelled || !inv) return;
        if (isInvoiceStatusPartial(inv)) {
          toast.warn("Partial invoices cannot be edited");
          navigate(`/invoices/view/${editId}`);
          return;
        }
        setInvoiceRecord(inv);
      } catch (err) {
        toast.error(err?.data?.message || "Failed to load invoice");
        navigate("/invoices");
      } finally {
        if (!cancelled) setLoadingPage(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, editId, navigate, fetchInvoiceById]);

  useEffect(() => {
    if (!isEdit) {
      hydratedInvoiceIdRef.current = null;
    }
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit || !invoiceRecord) return;

    const inv = invoiceRecord;
    const invoiceId = inv.id;
    if (hydratedInvoiceIdRef.current === invoiceId) return;
    hydratedInvoiceIdRef.current = invoiceId;

    const total =
      inv.total_amount != null && inv.total_amount !== ""
        ? String(inv.total_amount)
        : inv.amount != null
          ? String(inv.amount)
          : "";
    setAmount(total);
    setNote(inv.note ?? inv.description ?? "");
    setExpiresAt(parseExpiresAtFromApi(inv.expires_at));

    const oid = extractOrderId(inv);
    const orderObj =
      inv.order && typeof inv.order === "object" ? inv.order : null;
    if (oid) {
      if (orderObj) {
        setSelectedOrder({
          id: oid,
          label: formatOrderLabel({ ...orderObj, id: oid }),
          raw: orderObj,
        });
      } else {
        const opt = orderSelect.options.find((o) => o.id === oid);
        setSelectedOrder(
          opt ?? { id: oid, label: `Order ${shortId(oid)}`, raw: {} },
        );
      }
    } else {
      setSelectedOrder(null);
    }

    const ptid = extractPaymentTypeId(inv);
    const ptObj =
      (inv.paymentType &&
        typeof inv.paymentType === "object" &&
        inv.paymentType) ||
      (inv.payment_type &&
        typeof inv.payment_type === "object" &&
        inv.payment_type) ||
      null;
    if (ptid) {
      if (ptObj) {
        setSelectedPaymentType({
          id: ptid,
          label: ptObj.name ?? shortId(ptid),
          raw: ptObj,
        });
      } else {
        const opt = paymentTypeSelect.options.find((o) => o.id === ptid);
        setSelectedPaymentType(
          opt ?? {
            id: ptid,
            label: `Payment type ${shortId(ptid)}`,
            raw: {},
          },
        );
      }
    } else {
      setSelectedPaymentType(null);
    }
  }, [isEdit, invoiceRecord, orderSelect.options, paymentTypeSelect.options]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const clientErrors = {};
    if (!selectedOrder?.id) clientErrors.order_id = "Order is required";
    if (!selectedPaymentType?.id)
      clientErrors.payment_type_id = "Payment type is required";
    if (amount === "" || Number.isNaN(Number(amount)))
      clientErrors.amount = "Enter a valid amount";
    setErrors(clientErrors);
    if (Object.keys(clientErrors).length > 0) return;

    const values = {
      order_id: selectedOrder.id,
      payment_type_id: selectedPaymentType.id,
      amount,
      note,
      expires_at: formatExpiresAtForApi(expiresAt),
    };

    const fd = buildFormData(values);

    setSubmitting(true);
    try {
      // No relations on write requests — the page navigates away from the response
      if (isEdit) {
        await updateInvoice({ id: editId, data: fd }).unwrap();
        toast.success("Invoice updated");
      } else {
        await createInvoice({ data: fd }).unwrap();
        toast.success("Invoice added");
      }
      navigate("/invoices");
    } catch (err) {
      applyApiError(
        err,
        isEdit ? "Failed to update invoice" : "Failed to add invoice",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingPage) {
    return (
      <PageLayout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={320}
        >
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mb: 2,
            alignItems: "center",
          }}
        >
          {isEdit && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/invoices")}
            >
              Back to invoices
            </Button>
          )}
          {isOrderLocked && !isEdit && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/orders")}
              color="inherit"
            >
              Back to orders
            </Button>
          )}
        </Box>
        <Typography
          variant="h4"
          sx={{ mb: 3, fontWeight: 600, color: "primary.main" }}
        >
          {isEdit ? "Edit invoice" : "Add invoice"}
        </Typography>

        {isEdit &&
          invoiceRecord &&
          !extractOrderId(invoiceRecord) &&
          !extractPaymentTypeId(invoiceRecord) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This invoice response does not include <strong>order_id</strong> or{" "}
              <strong>payment_type_id</strong>—or their relations (
              <code>order</code>, <code>paymentType</code>). Ask the backend to
              attach those IDs or eager‑load relations on GET{" "}
              <code>/invoices/:id</code> so the selects can populate.
            </Alert>
          )}

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {isOrderLocked && (
                <Grid item xs={12}>
                  {lockedOrderResolving && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        py: 1,
                      }}
                    >
                      <CircularProgress size={24} />
                      <Typography color="text.secondary">
                        Loading order…
                      </Typography>
                    </Box>
                  )}
                  {!lockedOrderResolving && selectedOrder && (
                    <Alert severity="info" sx={{ py: 1.5 }}>
                      <strong>Order:</strong> {selectedOrder.label}
                    </Alert>
                  )}
                  {!lockedOrderResolving && !selectedOrder && (
                    <Alert severity="error">
                      This order could not be loaded. Return to the{" "}
                      <Typography
                        component="span"
                        sx={{ textDecoration: "underline", cursor: "pointer" }}
                        onClick={() => navigate("/orders")}
                      >
                        orders list
                      </Typography>
                      .
                    </Alert>
                  )}
                </Grid>
              )}
              {!isOrderLocked && (
                <Grid item xs={12}>
                  <Autocomplete
                    options={orderSelect.options}
                    value={selectedOrder}
                    onChange={(_, v) => {
                      setSelectedOrder(v);
                      clearError("order_id");
                    }}
                    onOpen={loadOrders}
                    loading={orderSelect.loading}
                    getOptionLabel={(opt) =>
                      opt?.label != null ? opt.label : ""
                    }
                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Order"
                        required
                        placeholder="Select order"
                        fullWidth
                        {...fieldProps("order_id")}
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={paymentTypeSelect.options}
                  value={selectedPaymentType}
                  onChange={(_, v) => {
                    setSelectedPaymentType(v);
                    clearError("payment_type_id");
                  }}
                  onOpen={handlePaymentTypeOpen}
                  loading={paymentTypeSelect.loading}
                  getOptionLabel={(opt) =>
                    opt?.label != null ? opt.label : ""
                  }
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Payment type"
                      required
                      fullWidth
                      {...fieldProps("payment_type_id")}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Amount"
                  type="number"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    clearError("amount");
                  }}
                  required
                  fullWidth
                  inputProps={{ step: "0.01", min: 0 }}
                  {...fieldProps("amount")}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Expires at"
                  type="date"
                  value={expiresAt}
                  onChange={(e) => {
                    setExpiresAt(e.target.value);
                    clearError("expires_at");
                  }}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  {...fieldProps("expires_at")}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Note"
                  value={note}
                  onChange={(e) => {
                    setNote(e.target.value);
                    clearError("note");
                  }}
                  fullWidth
                  multiline
                  minRows={2}
                  {...fieldProps("note")}
                />
              </Grid>

              <Grid item xs={12} sx={{ display: "flex", gap: 2, mt: 1 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={
                    submitting ||
                    (isOrderLocked && (lockedOrderResolving || !selectedOrder))
                  }
                >
                  {submitting ? (
                    <CircularProgress size={22} />
                  ) : isEdit ? (
                    "Save changes"
                  ) : (
                    "Add invoice"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate("/invoices")}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateInvoice;
